import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from '../lib/Helper';
import Canvas from './Canvas';
import { PanoramaVFTConfig, PanoramaVFTSceneConfig, PanoramaVFTPortalConfig, PanoramaVFTInfospotConfig } from '../models/PanoramaVFTConfig';
import './Panorama.scss';

declare global {
    interface Window {
        THREE:any;
        PANOLENS:any;
        hotspotTrackingStatus:any;
    }
}

type PanoramaProps = {
    inEditMode: boolean;
    globalConfig: PanoramaVFTConfig;
    updateCanvasContext: (ctx:any) => void;
    addInfospot: (x:number, y:number, z:number, panorama:string) => PanoramaVFTInfospotConfig;
};

type Scene = {
    hotspots: PanoramaVFTInfospotConfig[];
    portals: string[];
    panorama:any;
}

type PanoramaHotspot = {
    infospot: any;
    isScaledUp: boolean;
    updateHotSpotTextureUnvisited?: () => void;
    updateHotSpotTextureSelected?: () => void;
    updateHotSpotTextureVisited?: () => void;
}

type panoramaState = {
    tracking:Map<string,boolean>;
    scenes:Map<string,Scene>;
    viewer:any;
    isInitialized:boolean;
}

function Panorama({ inEditMode, globalConfig, updateCanvasContext, addInfospot}: PanoramaProps) {

    const wrapperRef = useRef<any>();
    const canvasRef = useRef<any>();
    const panoramaContainer = useRef<any>();
    const overlayRef = useRef<any>();
    const isDragging = useRef(false);

    const [panoramaState, setPanoramaState] = useState<panoramaState>({
        tracking: new Map<string,boolean>(),
        scenes: new Map<string,Scene>(),
        viewer: null,
        isInitialized: false
    });
    const panoramaStateRef = useRef(panoramaState);
    const [isCanvasInitialized, setIsCanvasInitialized] = useState(false);
    const [panoramaHeight, setPanoramaHeight] = useState(0);

    let tracking:Map<string,boolean> = new Map<string,boolean>();
    let hotspots:Map<string,PanoramaHotspot> = new Map<string,PanoramaHotspot>();
    let scenes:Map<string,Scene> = new Map<string,Scene>();
    let threeLibrary = window.THREE;
    let panoLibrary = window.PANOLENS;
    let viewer:any = null;

    const onMouseDown = () => {
        isDragging.current = true;
        if (canvasRef?.current?.canvasIframe?.current) {
            canvasRef?.current?.canvasIframe?.current.classList.add('pointer-events-none');
        }
    };

    const onMouseUp = () => {
        isDragging.current = false;
        if (canvasRef?.current?.canvasIframe?.current) {
            canvasRef?.current?.canvasIframe?.current.classList.remove('pointer-events-none');
        }
        let panoramaViewer = !isEmpty(viewer) ? viewer : panoramaStateRef.current?.viewer;
        if (panoramaViewer) {
            panoramaViewer.HANDLER_WINDOW_RESIZE();
        }
    };

    const onMouseMove = (e:any) => {
        if (!isDragging.current) return;

        // Get the total container height
        const containerHeight = wrapperRef.current.getBoundingClientRect().height;

        // Calculate the new height of the top panel
        const newTopHeight = e.clientY - wrapperRef.current.getBoundingClientRect().top;

        // Set the new height, but make sure it doesn't exceed boundaries
        if (newTopHeight > 50 && newTopHeight < containerHeight - 50) {
            setPanoramaHeight(newTopHeight);
        }
    };

    useEffect(() => {

        if (!panoramaState.isInitialized && isCanvasInitialized && Object.keys(globalConfig).length > 0) {
            if (panoramaContainer && panoramaContainer.current) {
                panoramaContainer.current.innerHTML = '';
            }   
            InitializePanorama();
        }

        if (globalConfig.canvasDisplayMode === "split") {
            if (wrapperRef.current && panoramaHeight === 0) {
                setPanoramaHeight(Math.floor(wrapperRef.current.getBoundingClientRect().height * 0.7));
            }
        } else {
            const overlay = overlayRef.current;
            const handle = overlay.querySelector('.resize-handle');
        
            const onHandleMove = (e:any) => {
                const newHeight = overlay.offsetHeight - e.movementY;
                const newWidth = overlay.offsetWidth + e.movementX;
                if (newHeight >= 100 && newHeight <= wrapperRef.current.offsetHeight) {
                    overlay.style.height = `${newHeight}px`;
                }
                if (newWidth >= 100 && newWidth <= wrapperRef.current.offsetWidth) {
                    overlay.style.width = `${newWidth}px`;
                }
            };
        
            const onHandleUp = () => {
                if (canvasRef?.current?.canvasIframe?.current) {
                    canvasRef?.current?.canvasIframe?.current.classList.remove('pointer-events-none');
                }
                document.removeEventListener('mousemove', onHandleMove);
                document.removeEventListener('mouseup', onHandleUp);
            };

            const onHandleDown = () => {
                if (canvasRef?.current?.canvasIframe?.current) {
                    canvasRef?.current?.canvasIframe?.current.classList.add('pointer-events-none');
                }
                document.addEventListener('mousemove', onHandleMove);
                document.addEventListener('mouseup', onHandleUp);
            };
        
            handle.addEventListener('mousedown', onHandleDown);
        
            return () => {
                handle.removeEventListener('mousedown', onHandleDown);
            };
        }

        /**
         * Changes to panoramaHeight shouldn't cause the componenet to rerender (handled by viewer already). Simpler to just disable the lint.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [globalConfig, isCanvasInitialized])

    useEffect(() => {
        if (panoramaState.isInitialized) {
            panoramaStateRef.current = panoramaState;
        }
    }, [panoramaState])

    const InitializePanorama = () => {
        let config:any;
        let camera:any;
        let control:any;
        let radius:number = 5000;
        let onloadPortal:PanoramaVFTPortalConfig | undefined = undefined;
        let hotspot:any; 
        let portal:any;

        if (panoramaContainer && panoramaContainer.current !== null) {
            SetupLoadHotspotTrackingListener();

            //fov, aspect ratio, near, far
            camera = new threeLibrary.PerspectiveCamera(globalConfig.fov ?? 90,
                panoramaContainer.current.offsetWidth / panoramaContainer.current.offsetHeight, 1, radius * 3);
            camera.rotation.order = "YXZ";

            config = {
                container: panoramaContainer.current,
                controlButtons: ['fullscreen'],
                camera: camera,
                autoHideInfospot: false
            };

            if (inEditMode) {
                config.output = 'overlay';

                panoLibrary.Viewer.prototype.addOutputElement = function () {
                    var element = document.createElement('div');
                    element.style.position = 'absolute';
                    element.style.left = '0px';
                    element.style.top = '50px';
                    element.style.fontSize = "15px";
                    element.style.padding = "20px";
                    element.style.backgroundColor = "#333333";
                    element.style.color = "#fff";
                    this.container.appendChild(element);
                    
                    var locationElement = document.createElement('p');
                    element.appendChild(locationElement);

                    var helperElement = document.createElement('p');
                    helperElement.textContent = "(Use Ctrl+Click to generate a hotspot at the cursor location)";
                    element.appendChild(helperElement);

                    this.outputDivElement = locationElement;
                };
            }

            viewer = new panoLibrary.Viewer(config);

            var blankPanorama = new panoLibrary.ImagePanorama("images/blank.png");
            scenes.set("blank", {
                portals: [],
                hotspots: [],
                panorama: blankPanorama
            });

            //adding a blank panorama as default to remove limbo infospots
            viewer.add(blankPanorama);

            control = viewer.getControl();

            //control state is a private variable and the only way to publicly modify it is via reset()
            document.addEventListener("mouseout", function () {
                control.target0.copy(control.target);
                control.position0.copy(control.object.position);
                control.zoom0 = control.object.zoom;

                if (control.position0.x !== 0 && control.position0.y !== 0 && control.position0.z !== 0) {
                    control.reset();
                }
            });

            //setting momentum scaling to a low factor. Default is-0.005
            control.momentumScalingFactor = -0.001;

            //one rotation per 30s
            control.autoRotateSpeed = 1;

            if (globalConfig.autoRotate) {
                control.autoRotate = true;
                var disableAutoRotate = function () {
                    var widget = viewer.widget;
                    if (widget.barElement) {
                        var autoRotateControl = widget.barElement.getElementsByClassName("autoRotateControl");
                        if (autoRotateControl && autoRotateControl.length) {
                            autoRotateControl[0].click();
                        }
                    }

                    panoramaContainer.current.removeEventListener("click", disableAutoRotate);
                };

                panoramaContainer.current.addEventListener("click", disableAutoRotate);
            }

            AddAutoRotateControl();
            AddManualDollyControl();

            if (inEditMode) {
                viewer.OUTPUT_INFOSPOT = true;

                //generate hotspot at cursor location on ctrl+click
                panoramaContainer.current.addEventListener('click', function (event:any) {
                    if (event.ctrlKey) {
                        var outputPosition = GetCursorPosition();
                        if (!isEmpty(outputPosition)) {
                            var hotspotNode = addInfospot(
                                parseInt(outputPosition.x, 10), 
                                parseInt(outputPosition.y, 10), 
                                parseInt(outputPosition.z, 10), 
                                viewer.panorama?.name);
                            var hotspot = GenerateHotspot(hotspotNode);

                            hotspots.set(hotspotNode.eventName, hotspot);
                            scenes.get(hotspotNode.scene)!.panorama.add(hotspot.infospot);

                            //force the panorama to refresh
                            viewer.panorama.toggleInfospotVisibility(true);
                            viewer.panorama.traverse((object:any) => {
                                object.dispatchEvent( { type: 'dismiss' } );
                            });
                        }
                    }
                });
            }

            var i, childNode;

            for (i = 0; i < globalConfig.scenes.length; i++) {
                childNode = globalConfig.scenes[i];
                if (childNode.eventType === "scene") {
                    scenes.set(childNode.eventName, {
                        hotspots: [],
                        portals: [],
                        panorama: GenerateScene(childNode)
                    });
                }
            }

            for (i = 0; i < globalConfig.infoSpots.length; i++) {
                childNode = globalConfig.infoSpots[i];

                if (childNode.eventType === "hotspot" && !isEmpty(childNode.eventName)) {

                    hotspot = GenerateHotspot(childNode);
                    hotspots.set(childNode.eventName, hotspot);
                    scenes.get(childNode.scene)?.hotspots.push(childNode);
                    scenes.get(childNode.scene)?.panorama.add(hotspot.infospot);

                } else if (childNode.eventType === "portal") {
                    portal = GeneratePortal(childNode);
                    portal.infospot.addEventListener('click', canvasRef.current.OnInfoPortalClick.bind(null, childNode.target));
                    scenes.get(childNode.scene)?.panorama.add(portal.infospot);
                } 
            }

            for (i = 0; i < globalConfig.vftPortals.length; i++) {
                childNode = globalConfig.vftPortals[i];
                if (childNode.eventType === "vftPortal") {
                    scenes.get(childNode.target)?.portals.push(childNode.eventName);
                    if (childNode.eventName === globalConfig.onloadPortal) {
                        onloadPortal = childNode;
                    }
                }
            }

            CheckScenesVisited();

            if (!isEmpty(onloadPortal)) {
                SwitchPanorama(onloadPortal!);
            }

            control.addEventListener('change', canvasRef.current.OnCameraRotate.bind(null, viewer));

            panoramaContainer.current.dispatchEvent(new CustomEvent("initialized"));

            setPanoramaState(prevState => ({
                ...prevState,
                scenes: scenes,
                viewer: viewer,
                isInitialized: true
            }));
        };
    }

    const SetupLoadHotspotTrackingListener = () => {
        if (window.hotspotTrackingStatus) {
            for (var eventName in window.hotspotTrackingStatus) {
                tracking.set(eventName, window.hotspotTrackingStatus[eventName]);
            }
        }
        setPanoramaState(prevState => ({
            ...prevState,
            tracking: tracking
        }));
    }

    const UpdateHotspotState = (hotspot:PanoramaVFTInfospotConfig, visited:boolean) => {
        tracking.set(hotspot.eventName, visited);
        setPanoramaState(prevState => ({
            ...prevState,
            tracking: tracking
        }));
        document.dispatchEvent(new CustomEvent("hotspotStateChange", {
            detail: {
                hotspot: hotspot.eventName,
                visited: visited
            }
        }));
    }

    const AddAutoRotateControl = () => {
        var widget, control:any, onChange, play:any, pause:any, controlWidget;

        play = panoLibrary.DataImage.VideoPlay;
        pause = panoLibrary.DataImage.VideoPause;

        widget = viewer.widget;
        control = viewer.getControl();

        onChange = function (this:any, e:any) {
            e.stopPropagation();
            control.autoRotate = !control.autoRotate;
            this.style.backgroundImage = 'url("' + (control.autoRotate ? pause : play) + '")';
        };

        controlWidget = widget.createCustomItem({
            style: {
                backgroundImage: 'url("' + (control.autoRotate ? pause : play) + '")'
            },
            onTap: onChange
        });
        controlWidget.className = "autoRotateControl";

        widget.barElement.appendChild(controlWidget);
    }

    const AddManualDollyControl = () => {
        var widget, camera:any, control:any, dollyIn, dollyOut, onDollyIn, onDollyOut, increment = 5;
        var dollyInWidget, dollyOutWidget;

        dollyIn = "./images/dollyIn.svg";
        dollyOut = "./images/dollyOut.svg";

        widget = viewer.widget;
        camera = viewer.getCamera();
        control = viewer.getControl();

        onDollyIn = function (e:any) {
            e.stopPropagation();
            if ((camera.fov - increment) > control.minFov) {
                viewer.setCameraFov(camera.fov - increment);
            }
        };

        onDollyOut = function (e:any) {
            e.stopPropagation();
            if ((camera.fov + increment) < control.maxFov) {
                viewer.setCameraFov(camera.fov + increment);
            }
        };

        dollyInWidget = widget.createCustomItem({
            style: {
                backgroundImage: 'url("' + dollyOut + '")'
            },
            onTap: onDollyOut
        });
        dollyInWidget.className = "dollyInControl";

        widget.barElement.appendChild(dollyInWidget);

        dollyOutWidget = widget.createCustomItem({
            style: {
                backgroundImage: 'url("' + dollyIn + '")'
            },
            onTap: onDollyIn
        });
        dollyOutWidget.className = "dollyOutControl";

        widget.barElement.appendChild(dollyOutWidget);
    }

    const GenerateScene = (node:PanoramaVFTSceneConfig):any => {
        var src, scene;

        src = node.sourceAsset;

        scene = new panoLibrary.ImagePanorama(src);
        scene.animationDuration = 100;
        scene.name = node.eventName;

        viewer.add(scene);
        return scene;
    }

    const GenerateHotspot = (node:PanoramaVFTInfospotConfig): PanoramaHotspot => {

        var radius = 5000, sourceSrc, selectedSrc, visitedSrc, position;

        if (isEmpty(node.sourceAsset) || (!isEmpty(node.sourceAsset) &&
            node.sourceAsset.substr(node.sourceAsset.length - 7, 7).toLowerCase() === "images/")) {
            sourceSrc = "./images/uns.png";
        } else {
            sourceSrc = node.sourceAsset;
        }

        if (isEmpty(node.selectedAsset) || (!isEmpty(node.selectedAsset) &&
            node.selectedAsset.substr(node.selectedAsset.length - 7, 7).toLowerCase() === "images/")) {
            selectedSrc = "./images/sel.png";
        } else {
            selectedSrc = node.selectedAsset;
        }

        if (isEmpty(node.visitedAsset) || (!isEmpty(node.visitedAsset) &&
            node.visitedAsset.substr(node.visitedAsset.length - 7, 7).toLowerCase() === "images/")) {
            visitedSrc = "./images/vis.png";
        } else {
            visitedSrc = node.visitedAsset;
        }

        //textures need to have a unique url
        sourceSrc += "?_" + Math.random().toString(36).substr(2, 10);
        selectedSrc += "?_" + Math.random().toString(36).substr(2, 10);
        visitedSrc += "?_" + Math.random().toString(36).substr(2, 10);

        position = new threeLibrary.Vector3(
            node.x,
            node.y,
            node.z
        );

        var baseScale = globalConfig.baseScale * position.length() / radius;
        var hideInfoText = globalConfig.hideInfoText;

        let hotspot:PanoramaHotspot = {
            infospot: new panoLibrary.Infospot(baseScale, sourceSrc),
            isScaledUp: true,
            updateHotSpotTextureUnvisited: () => {},
            updateHotSpotTextureSelected: () => {},
            updateHotSpotTextureVisited: () => {}
        };

        hotspot.infospot.position.copy(position);

        hotspot.updateHotSpotTextureUnvisited = UpdateHotSpotTexture.bind(null, hotspot.infospot, sourceSrc);
        hotspot.updateHotSpotTextureSelected = UpdateHotSpotTexture.bind(null, hotspot.infospot, selectedSrc);
        hotspot.updateHotSpotTextureVisited = UpdateHotSpotTexture.bind(null, hotspot.infospot, visitedSrc);

        if (!isEmpty(node.infoText) && !hideInfoText) {
            //default vertical distance between text and infospot is 40 units
            if (!hotspot.infospot.element) {
                hotspot.infospot.addHoverText(node.infoText, node.infoTextDelta ?? 50);
            }
        }

        hotspot.infospot.addEventListener('hoverenter', function (hotspot:PanoramaHotspot) {
            hotspot.isScaledUp = true;
        }.bind(null, hotspot));

        hotspot.infospot.addEventListener('click', function (hotspot:PanoramaHotspot, hotspotNode:PanoramaVFTInfospotConfig) {

            if (hotspot.isScaledUp) {
                hotspot.infospot.scaleUpAnimation.stop();
                hotspot.infospot.scaleDownAnimation.start();
                hotspot.isScaledUp = false;
            } else {
                hotspot.infospot.scaleDownAnimation.stop();
                hotspot.infospot.scaleUpAnimation.start();
                hotspot.isScaledUp = true;
            }

            /** Set hotspot to visited, send status upstream */
            UpdateHotspotState(hotspotNode, true);
            CheckPortalVisited(hotspotNode);

            if (scenes.has(hotspotNode.scene)) {
                // map.get() always returns a nullable type. Use map.get()! to get the value
                UpdateSceneHotspotTextures(scenes.get(hotspotNode.scene)!);
            }
            hotspot.updateHotSpotTextureSelected?.();

        }.bind(null, hotspot, node));

        return hotspot;
    }

    const UpdateSceneHotspotTextures = (scene:Scene) => {
        if (scene.hotspots && scene.hotspots.length) {
            scene.hotspots.forEach(function (hotspot) {
                if (IsHotspotVisited(hotspot)) {
                    hotspots.get(hotspot.eventName)!.updateHotSpotTextureVisited?.();
                }
            });
        }
    }

    const UpdateHotSpotTexture = (infospot:any, texture:any) => {
        panoLibrary.TextureLoader.load(texture, function (infospotScope:any, texture:any) {
            texture.wrapS = threeLibrary.RepeatWrapping;
            texture.repeat.x = - 1;

            infospotScope.material.side = threeLibrary.DoubleSide;
            infospotScope.material.map = texture;
            infospotScope.material.depthTest = false;
            infospotScope.material.needsUpdate = true;

        }.bind(null, infospot));
    }

    const IsHotspotVisited = (hotspot:PanoramaVFTInfospotConfig) => {
        if (tracking && hotspot && tracking.get(hotspot.eventName)) {
            return true;
        }
        return false;
    }

    const GeneratePortal = (node:PanoramaVFTInfospotConfig): PanoramaHotspot => {
        var radius = 5000, sourceSrc, src, position;

        if (isEmpty(node.sourceAsset) || (!isEmpty(node.sourceAsset) &&
            node.sourceAsset.substr(node.sourceAsset.length - 7, 7).toLowerCase() === "images/")) {
            sourceSrc = "./images/uns.png";
        } else {
            sourceSrc = node.sourceAsset;
        }

        src = sourceSrc + "?_" + Math.random().toString(36).substr(2, 10);

        position = new threeLibrary.Vector3(
            node.x,
            node.y,
            node.z
        );

        let portal:PanoramaHotspot = {
            infospot: new panoLibrary.Infospot(globalConfig.baseScale * position.length() / radius, src),
            isScaledUp: false
        };

        portal.infospot.position.copy(position);

        if (!isEmpty(node.infoText)) {
            portal.infospot.addHoverText(node.infoText,  node.infoTextDelta ?? 50);
        }

        return portal;
    }

    const SwitchPanorama = (node:PanoramaVFTPortalConfig) => {
        let panoramaViewer = !isEmpty(viewer) ? viewer : panoramaStateRef.current?.viewer;
        let panoramaScenes = !isEmpty(scenes) ? scenes : panoramaStateRef.current?.scenes;

        if (!isEmpty(panoramaViewer) && !isEmpty(node.target)) {
            var target = panoramaScenes.get(node.target)!;
            if (!isEmpty(target)) {

                //Dismiss infospots
                panoramaViewer.panorama.traverse((object:any) => {
                    object.dispatchEvent( { type: 'dismiss' } );
                });

                //Set panorama once current panoarama has been loaded to avoid infospot visibility bugs
                if (!panoramaViewer.panorama.loaded) {
                    var onViewerLoadSetPanorama = function () {
                        SetPanorama(target.panorama, node.degree);
                        //config once not supported in IE11
                        panoramaViewer.panorama.removeEventListener("load", onViewerLoadSetPanorama);
                    };
                    panoramaViewer.panorama.addEventListener("load", onViewerLoadSetPanorama);

                    // Sometimes the load listener doesn't trigger. Add a fallback just in case
                    setTimeout(function() {
                        if (!panoramaViewer.panorama.loaded) {
                            SetPanorama(target.panorama, node.degree);
                        }
                    }, 3000);
                } else {
                    SetPanorama(target.panorama, node.degree);
                }

                UpdateSceneHotspotTextures(target);
            }
        }
    }

    const SetPanorama = (target:any, degree:number) => {
        let panoramaViewer = !isEmpty(viewer) ? viewer : panoramaStateRef.current?.viewer;

        var onTargetLoad = function () {
            target.removeEventListener("load", onTargetLoad);
        };

        if (!target.loaded) {
            target.addEventListener("load", onTargetLoad);
        }

        panoramaViewer.setPanorama(target);
        if (!isEmpty(degree)) {
            RotatePanoramaCamera(degree * Math.PI / 180);
        }
    }

    const RotatePanoramaCamera = (end:number) => {
        let panoramaViewer = viewer || panoramaStateRef.current?.viewer;
        if (!isEmpty(panoramaViewer)) {
            var control = panoramaViewer.getControl();

            if (!isEmpty(control)) {
                //Adding a 90 degree offset to cancel out the -90 starting degree from threejs
                end += Math.PI / 2;
                var start = control.getAzimuthalAngle();
                var rotateBy = 0;

                /**
                 * Calculating the angular difference between start to end. Inputs are in radians
                 * Since the range of degrees are from -PI to PI, we need to append PI before modulus and subtract PI post modulus
                 */
                if (start > end) {
                    rotateBy = Math.PI - (end - start + Math.PI) % (Math.PI * 2);
                } else {
                    rotateBy = -Math.PI + (start - end + Math.PI) % (Math.PI * 2);
                }

                control.rotateLeft(rotateBy);
            }
        }
    }

    const CheckPortalVisited = (hotspot:PanoramaVFTInfospotConfig) => {
        var targetScene = hotspot.scene;
        if (!isEmpty(targetScene)) {
            var scene = scenes.get(targetScene)!;
            if (!isEmpty(scene)) {
                var allHotspotVisited = true;
                for (var i = 0; i < scene.hotspots.length; i++) {
                    var hotspotNode = scene.hotspots[i];
                    if (hotspot === hotspotNode) {
                        continue;
                    }

                    if (!IsHotspotVisited(hotspotNode)) {
                        allHotspotVisited = false;
                        break;
                    }
                }

                if (allHotspotVisited) {
                    canvasRef.current.SetPortalVisited(scene.portals);
                }
            }
        }
    }

    const CheckScenesVisited = () => {
        var key, scene;
        for (key in scenes) {
            scene = scenes.get(key)!;
            var sceneVisited = true;

            for (var i = 0; i < scene.hotspots.length; i++) {
                var hotspot = scene.hotspots[i];
                if (!IsHotspotVisited(hotspot)) {
                    sceneVisited = false;
                    break;
                }
            }

            if (sceneVisited) {
                canvasRef.current.SetPortalVisited(scene.portals);
            }
        }
    }

    const GetCursorPosition = () => {
        var intersects, point, outputPosition, targetVector;

        intersects = viewer.raycaster.intersectObject(viewer.panorama, true);
        targetVector = new threeLibrary.Vector3();

        if (intersects.length > 0) {

            point = intersects[0].point;
            viewer.panorama.getWorldPosition(targetVector);

            // Panorama is scaled -1 on X axis
            outputPosition = new threeLibrary.Vector3(
                -(point.x - targetVector.x).toFixed(2),
                (point.y - targetVector.y).toFixed(2),
                (point.z - targetVector.z).toFixed(2)
            );
        }

        return outputPosition;
    }

    const OnCanvasLoad = (ctx: any) => {
        setIsCanvasInitialized(true);
        updateCanvasContext(ctx);
    }

    return (
        <>
            {
                globalConfig.canvasDisplayMode === "split"
                    ?
                    <div className='panorama-wrapper flex flex-col'
                        ref={wrapperRef}
                        onMouseMove={onMouseMove}
                        onMouseUp={onMouseUp}
                        onMouseLeave={onMouseUp} >
                        <div className='panorama-parent' style={{ height: `${panoramaHeight}px` }}>
                            <div className='panoramaContainer' ref={panoramaContainer}></div>
                        </div>
                        <div className="separator" onMouseDown={onMouseDown}></div>
                        <div className="w-full flex-1 flex-grow" >
                            <Canvas globalConfig={globalConfig}
                                ref={canvasRef}
                                rotatePanoramaCamera={RotatePanoramaCamera}
                                switchPanorama={SwitchPanorama}
                                onCanvasLoad={OnCanvasLoad} />
                        </div>
                    </div>
                    :
                    <div className='flex flex-col h-full w-full overflow-hidden' ref={wrapperRef}>

                        <div className="panoramaContainer h-full" ref={panoramaContainer}></div>

                        <div className={`absolute bottom-0 left-0 rounded-md overflow-hidden 
                            selection:bg-transparent
                            w-[${globalConfig.canvasOverlayWidth}px]
                            h-[${globalConfig.canvasOverlayHeight}px]
                            min-w-[${globalConfig.canvasOverlayWidth}px]
                            min-h-[${globalConfig.canvasOverlayHeight}px]
                            max-h-full 
                            max-w-full`}
                            ref={overlayRef}
                            style={{width:`${globalConfig.canvasOverlayWidth}px`,height:`${globalConfig.canvasOverlayHeight}px`, backgroundColor: `${globalConfig.canvasOverlayBackground}` }}>

                            <Canvas 
                                globalConfig={globalConfig}
                                ref={canvasRef}
                                rotatePanoramaCamera={RotatePanoramaCamera}
                                switchPanorama={SwitchPanorama}
                                onCanvasLoad={OnCanvasLoad} 
                            />

                            <div className="resize-handle absolute top-0 right-0 w-4 h-4 bg-gray-500 cursor-ne-resize"></div>
                        </div>

                  </div>
            }
        </>
    );
}

export default Panorama;